import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  CreateResourceResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { WarehouseReceiptCollectionItemResponse } from '../responses/warehouse-receipt-collection-item.response';
import { WarehouseReceiptCreateRequest } from '../requests/warehouse-receipt-create.request';
import { WarehouseReceiptProductCollectionItemResponse } from '../responses/warehouse-receipt-product-collection-item.response';
import { blank, Guid } from '@web/utils';
import { WarehouseReceiptDetailsResponse } from '../responses/warehouse-receipt-details.response';
import { WarehouseReceiptUpdateRequest } from '../requests/warehouse-receipt-update.request';

@Injectable({
  providedIn: 'root',
})
export class WarehouseReceiptApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  /* prettier-ignore */
  public fetchCollection(request: CollectionRequest): Observable<CollectionResponse<WarehouseReceiptCollectionItemResponse>> {
    const path = 'admin/receipt';
    const url = this.provider.httpUrlFactory.withQueryParams(this.hostUrl(), path, this.provider.httpHelper.collectionRequestToQueryParams(request));
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToCollectionResponse(WarehouseReceiptCollectionItemResponse)));
  }

  /* prettier-ignore */
  public fetchReceiptProductCollection(receiptId: Guid, request: CollectionRequest): Observable<CollectionResponse<WarehouseReceiptProductCollectionItemResponse>> {
    const path = `admin/receipt/${HttpParams.Id}/good`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({ [HttpParams.Id]: receiptId })
        .queryParams(this.provider.httpHelper.collectionRequestToQueryParams(request))
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToCollectionResponse(WarehouseReceiptProductCollectionItemResponse)));
  }

  /* prettier-ignore */
  public fetchDetailsById(id: Guid): Observable<WarehouseReceiptDetailsResponse> {
    const path = `admin/receipt/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {[HttpParams.Id]: id});
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(WarehouseReceiptDetailsResponse)));
  }

  /* prettier-ignore */
  public approveById(id: Guid): Observable<void> {
    const path = `admin/receipt/${HttpParams.Id}/approve`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http
      .put(url, body,{ context })
      .pipe(blank());
  }

  /* prettier-ignore */
  public create(
    request: WarehouseReceiptCreateRequest,
  ): Observable<CreateResourceResponse> {
    const path = 'admin/receipt';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(request, WarehouseReceiptCreateRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .post(url, body, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(CreateResourceResponse)));
  }

  /* prettier-ignore */
  public update(id: Guid, request: WarehouseReceiptUpdateRequest): Observable<void> {
    const path = `admin/receipt/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const body = this.provider.httpHelper.requestToBody(request, WarehouseReceiptUpdateRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Warehouse;
  }
}
